/**
 * Squidex client (frontend).
 *
 * IMPORTANT: This module does NOT call Squidex directly.
 * It ONLY uses our serverless functions under /api/*.
 */

function getProxyContentUrl(schema, options = {}) {
  const params = new URLSearchParams()
  params.set('schema', schema)
  if (options.$top != null) params.set('$top', String(options.$top))
  if (options.$skip != null) params.set('$skip', String(options.$skip))
  // Squidex uses $orderby, our client uses $orderBy
  if (options.$orderBy) params.set('$orderby', options.$orderBy)
  return `/api/squidex-content?${params.toString()}`
}

function getProxyAssetUrl(assetId) {
  const params = new URLSearchParams()
  params.set('id', assetId)
  return `/api/squidex-asset?${params.toString()}`
}

/**
 * Flatten Squidex field value: { iv: x } or { de: x, en: y } -> value.
 * For invariant (iv) or first available language.
 */
function flattenValue(field) {
  if (field == null) return undefined
  if (typeof field === 'object' && 'iv' in field) return field.iv
  if (typeof field === 'object') {
    const keys = Object.keys(field)
    return keys.length ? field[keys[0]] : undefined
  }
  return field
}

/**
 * Normalize Squidex content item to Contentful-like shape so existing components keep working.
 * Squidex: { id, data: { title: { iv: "..." }, ... } }
 * Output:  { sys: { id, contentType: { sys: { id: schema } } }, fields: { title: "...", ... } }
 * Asset refs: resolve to full URL using BASE_URL/api/assets/{app}/{id}
 */
function normalizeItem(squidexItem, schema) {
  if (!squidexItem || !squidexItem.data) {
    return null
  }
  const data = squidexItem.data
  const fields = {}

  for (const [key, value] of Object.entries(data)) {
    const raw = flattenValue(value)
    if (raw === undefined) continue

    // Asset reference: Squidex assets fields return an array of asset ids.
    // IMPORTANT: Do NOT treat arbitrary strings as asset ids.
    const assetId = Array.isArray(raw) && raw.length ? raw[0] : null
    if (assetId) {
      fields[key] = {
        sys: { type: 'Asset', id: assetId },
        fields: { file: { url: getProxyAssetUrl(assetId) } },
      }
      continue
    }

    // Geolocation: Squidex uses latitude/longitude; components expect lat/lon
    if (raw && typeof raw === 'object' && ('latitude' in raw || 'longitude' in raw)) {
      fields[key] = {
        lat: raw.latitude ?? raw.lat,
        lon: raw.longitude ?? raw.lon,
      }
      continue
    }

    fields[key] = raw
  }

  return {
    sys: {
      id: squidexItem.id,
      contentType: { sys: { id: schema } },
    },
    fields,
  }
}

/**
 * Fetch contents for one schema.
 * @param {string} schema - e.g. 'event' | 'biografie'
 * @param {object} options - optional $top, $skip, $orderBy
 * @returns {Promise<{ items: Array }>} Contentful-like { items: normalized[] }
 */
export async function getContents(schema, options = {}) {
  const url = getProxyContentUrl(schema, options)
  const res = await fetch(url, { headers: { Accept: 'application/json' } })
  // Missing schema / not created yet: treat as empty list so the UI still loads.
  if (res.status === 404) {
    return { items: [] }
  }
  if (!res.ok) {
    const bodyText = await res.text().catch(() => '')
    throw new Error(
      `Squidex ${schema}: ${res.status} ${res.statusText}${bodyText ? ` — ${bodyText}` : ''}`,
    )
  }
  const json = await res.json()
  const items = Array.isArray(json.items) ? json.items : json.data ? [json] : []
  return {
    items: items.map((item) => normalizeItem(item, schema)).filter(Boolean),
  }
}

export { normalizeItem, flattenValue }
